<template>
  <InfoCard
    class="base-info-container"
    icon="iconfont icona-BasicInfo"
    :title="$t(`${i18nPrefix}.title.baseInfo`)"
    :infoList="infoList"
    :infoLines="infoLines"
  >
    <template #email>
      <el-tooltip effect="light" popper-class="light-tooltip-popper" :content="baseInfo.email">
        <div class="content-email">{{ baseInfo.email }}</div>
      </el-tooltip>
    </template>
    <template #chatTools>
      <div v-for="(chatTool, index) in chatTools" :key="index">
        <div class="content-name">
          <el-tooltip effect="light" popper-class="light-tooltip-popper" :content="chatTool.label">
            <div class="content-name-label">{{ chatTool.label }}</div>
          </el-tooltip>
          <el-tooltip effect="light" popper-class="light-tooltip-popper" :content="chatTool.value">
            <div class="content-name-value">{{ chatTool.value }}</div>
          </el-tooltip>
        </div>
      </div>
    </template>
    <template #socialNetworks>
      <div v-for="(network, index) in socialNetworks" :key="index">
        <div class="content-name">
          <el-tooltip effect="light" popper-class="light-tooltip-popper" :content="network.label">
            <div class="content-name-label">{{ network.label }}</div>
          </el-tooltip>
          <el-tooltip effect="light" popper-class="light-tooltip-popper" :content="network.value">
            <div class="content-name-value">{{ network.value }}</div>
          </el-tooltip>
        </div>
      </div>
    </template>
  </InfoCard>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import InfoCard from '@/components/candidate/infoCard.vue'
import { getI18nPropertyValue, getConstantI18nValue } from '@/i18n'
import { CHAT_TOOL, SOCIAL_NETWORK } from '@/constant/meta'

export default {
  components: {
    InfoCard
  },
  props: {
    baseInfo: {
      required: true
    },
    hiddenFieldList: {
      type: Array,
      required: false
    }
  },
  data () {
    return {
      i18nPrefix: 'personal.profile',
      getI18nPropertyValue
    }
  },
  computed: {
    infoList () {
      return [this.baseInfo]
    },
    infoLines () {
      const vm = this
      let hiddenFieldList = []
      hiddenFieldList = _.concat(hiddenFieldList, vm.getHiddenField)
      const lines = {
        legalName: {
          labelI18nKey: `${this.i18nPrefix}.label.legalName`,
          dataKey: 'legal_name',
          multipleText: true,
          hidden: _.includes(hiddenFieldList, 'legalName')
        },
        homeLocation: {
          labelI18nKey: `${this.i18nPrefix}.label.homeLocation`,
          dataKey: 'home_location',
          formatter: (value) => {
            return vm.formatLocation(value)
          },
          hidden: _.includes(hiddenFieldList, 'homeLocation')
        },
        email: {
          labelI18nKey: `${this.i18nPrefix}.label.email`,
          contentName: 'email',
          hidden: _.includes(hiddenFieldList, 'email')
        },
        currentLocation: {
          labelI18nKey: `${this.i18nPrefix}.label.currentLocation`,
          dataKey: 'current_location',
          formatter: (value) => {
            return vm.formatLocation(value)
          },
          hidden: _.includes(hiddenFieldList, 'currentLocation')
        },
        mobileId: {
          labelI18nKey: `${this.i18nPrefix}.label.mobileId`,
          dataKey: 'mobile_id',
          hidden: _.includes(hiddenFieldList, 'mobileId')
        },
        address: {
          labelI18nKey: `${this.i18nPrefix}.label.address`,
          dataKey: 'address',
          hidden: _.includes(hiddenFieldList, 'address')
        },
        salutation: {
          labelI18nKey: `${this.i18nPrefix}.label.salutation`,
          formatter: (val) => {
            const i18nKey = _.findKey({ Mr: 'MR', Mrs: 'MRS', Ms: 'MS' }, (value) => value === val)
            return i18nKey && vm.$t(`${this.i18nPrefix}.label.${i18nKey}`)
          },
          hidden: _.includes(hiddenFieldList, 'salutation')
        },
        otherAddress: {
          labelI18nKey: `${this.i18nPrefix}.label.otherAddress`,
          dataKey: 'other_address',
          hidden: _.includes(hiddenFieldList, 'otherAddress')
        },
        birthday: {
          labelI18nKey: `${this.i18nPrefix}.label.birthday`,
          formatter: (val, info) => {
            const birthday = moment(val).format('DD/MM/YYYY')
            return val && `${birthday}`
          },
          hidden: _.includes(hiddenFieldList, 'birthday')
        },
        phone: {
          labelI18nKey: `${this.i18nPrefix}.label.phone`,
          dataKey: 'phone',
          hidden: _.includes(hiddenFieldList, 'phone')
        },
        gender: {
          labelI18nKey: `${this.i18nPrefix}.label.gender`,
          formatter: (val) => {
            const i18nKey = _.findKey({ male: 'M', female: 'F', nonBinary: 'N', preferNotToDisclose: 'P' }, (value) => value === val)
            return i18nKey && vm.$t(`common.gender.${i18nKey}`)
          },
          hidden: _.includes(hiddenFieldList, 'gender')
        },
        secondPhone: {
          labelI18nKey: `${this.i18nPrefix}.label.secondPhone`,
          dataKey: 'second_phone',
          hidden: _.includes(hiddenFieldList, 'secondPhone')
        },
        chatTools: {
          labelI18nKey: `${this.i18nPrefix}.label.chatId`,
          contentName: 'chatTools',
          hidden: _.includes(hiddenFieldList, 'chatId')
        },
        expectedSalary: {
          labelI18nKey: `${this.i18nPrefix}.label.expectedSalary`,
          dataKey: 'expected_salary',
          formatter: (val, line) => {
            return val ? `${val} ${line.currency ? `(${line.currency})` : ''}` : ''
          },
          hidden: _.includes(hiddenFieldList, 'expectedSalary')
        },
        socialNetworks: {
          labelI18nKey: `${this.i18nPrefix}.label.socialNetwork`,
          contentName: 'socialNetworks',
          hidden: _.includes(hiddenFieldList, 'socialNetwork')
        }
      }
      return lines
    },
    chatTools () {
      const chatTools = []
      _.forEach(JSON.parse(this.baseInfo.chat_tools), (accountList, tool) => {
        let toolI18n = getConstantI18nValue(CHAT_TOOL, tool, 'meta.CHAT_TOOL')
        if (toolI18n === 'null') {
          toolI18n = null
        }
        _.forEach(accountList, account => {
          chatTools.push({ label: toolI18n, value: account })
        })
      })
      return chatTools
    },
    socialNetworks () {
      const socialNetWorks = []
      _.forEach(JSON.parse(this.baseInfo.social_net_works), (accountList, netWork) => {
        let netWorkI18n = getConstantI18nValue(SOCIAL_NETWORK, netWork, 'meta.SOCIAL_NETWORK')
        if (netWorkI18n === 'null') {
          netWorkI18n = null
        }
        _.forEach(accountList, account => {
          socialNetWorks.push({ label: netWorkI18n, value: account })
        })
      })
      return socialNetWorks
    }
  },
  created () {

  },
  methods: {
    formatLocation (location, info) {
      if (!location) {
        return
      }
      return _.chain(['country', 'province', 'city']).map(key => {
        const val = _.get(location, key)
        if (!val) {
          return
        }
        return getI18nPropertyValue(val, 'cn_name', 'name')
      }).filter().reverse().join(' ').value()
    }
  }
}
</script>

<style lang="scss" scoped>
.base-info-container {
  .content-name {
    width: 100%;
    white-space: nowrap;

    .content-name-label {
      display: inline-block;
      vertical-align: middle;
      margin-right: 4px;
      width: 35%;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .content-name-value {
      display: inline-block;
      vertical-align: middle;
      max-width: 65%;
      text-overflow: ellipsis;
      overflow: hidden;
    }
  }

  .content-email {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  ::v-deep {
    .card-title-container {
      .title {
        color: #303133;
        font-weight: 500;
      }

      .title-icon {
        font-size: 26px;
      }
    }

    .card-list {
      .info-line-list {
        .info-line {
          .info-label {
            font-weight: 500;
          }
        }
      }

      .info-card {
        padding-left: 20px;
        padding-right: 20px;
      }
    }
  }
}
</style>
